/**
 * Session-keyed records for SciSearch AI. Each session accumulates everything scoped to it —
 * provenance of the applied search, the conversation transcript, follow-up suggestions, and the
 * feedback data — at the moment it exists. The slice's pointers (pendingSessionId,
 * activeSessionId, confirmedSessionId) decide which record the UI sees; ending a session moves a
 * pointer, and the record becomes unreachable instead of stale-but-visible.
 */

export const MAX_SESSIONS = 10;

export const emptySessions = {
  byId: {},
  order: [],
};

export function emptySessionRecord(sessionId) {
  return {
    sessionId,
    // Applied-search provenance; null until the session is activated (accept) or resumed
    // (AI saved-search load). Stored as one object so getAISearchData keeps a stable
    // reference for useSelector consumers.
    provenance: null,
    // Feedback-subject data: the query chain (original + applied follow-ups), the raw LLM
    // interpretation, and the lifecycle timestamps.
    naturalLanguageQuery: { queries: [] },
    filterResponse: null,
    generatedAt: null,
    confirmedAt: null,
    appliedFollowUps: [],
    // Session UI state.
    conversationMessages: [],
    followUps: { sessionId, fingerprint: null, queries: [], count: 0 },
    // Feedback telemetry: the event log and aggregates POSTed to /api/ai/search_feedback.
    feedback: {
      queries: [],
      events: [],
      processingTimeMs: 0,
      searchResultsCount: 0,
      searchType: '',
      filters: {},
    },
  };
}

/**
 * Upsert one session record and return the new sessions map. `update` receives the existing
 * record (or a fresh one) and returns the fields to merge. Evicts the oldest records beyond
 * MAX_SESSIONS, never evicting the record being written or any record a slice pointer targets.
 * @param {object} state - full searchNLP slice state (pointers are read for the keep set)
 * @param {string} sessionId
 * @param {(existing: object) => object} update
 * @returns {{byId: object, order: string[]}}
 */
export function withSession(state, sessionId, update) {
  if (!sessionId) {
    return state.sessions;
  }
  const existing =
    state.sessions.byId[sessionId] || emptySessionRecord(sessionId);
  const record = { ...existing, ...update(existing) };

  const isNew = !state.sessions.byId[sessionId];
  const byId = { ...state.sessions.byId, [sessionId]: record };
  let order = isNew
    ? [...state.sessions.order, sessionId]
    : state.sessions.order;

  if (order.length > MAX_SESSIONS) {
    const keep = new Set([
      sessionId,
      state.pendingSessionId,
      state.activeSessionId,
      state.confirmedSessionId,
    ]);
    const evictable = order.filter(id => !keep.has(id));
    const evicted = new Set(evictable.slice(0, order.length - MAX_SESSIONS));
    order = order.filter(id => !evicted.has(id));
    for (const id of evicted) {
      delete byId[id];
    }
  }

  return { byId, order };
}
